// Aggregate "open risk" surface for the home page. Sum of cash-at-risk if every
// stop fires, plus a count of positions with no stop at all. Andy's #1 ask:
// before adding more risk, know the existing total. The stopCovered logic lives
// in PositionsTable.tsx but the aggregate belonged on the dashboard.

import { Link } from '@tanstack/react-router';
import { ShieldAlert, ShieldOff } from 'lucide-react';
import type { ActiveOrder } from '@t212/shared';
import type { PositionWithTv } from '@/lib/api';
import { fmtMoney } from '@/lib/format';
import { cn } from '@/lib/cn';

type Props = {
  positions: PositionWithTv[];
  activeOrders: ActiveOrder[];
};

export function OpenRiskStrip({ positions, activeOrders }: Props) {
  // Map ticker → protective stop price for long positions.
  const stops = new Map<string, number>();
  for (const o of activeOrders) {
    if ((o.type === 'STOP' || o.type === 'STOP_LIMIT') && o.side === 'SELL') {
      if (typeof o.stopPrice === 'number') {
        stops.set(o.ticker, o.stopPrice);
      }
    }
  }

  let protectedRiskGbp = 0;
  let unprotectedRiskGbp = 0;
  let unprotectedCount = 0;
  let invalidStopCount = 0; // stop above current price for a long

  for (const p of positions) {
    const stop = stops.get(p.instrument.ticker);
    const currentValue = p.walletImpact.currentValue;
    if (typeof stop === 'number' && stop > 0) {
      if (stop >= p.currentPrice) {
        // Incoherent: stop above market on a long is already-triggered or
        // misconfigured. Treat as unprotected for risk accounting and flag.
        invalidStopCount += 1;
        unprotectedRiskGbp += currentValue;
        unprotectedCount += 1;
      } else {
        const perShareRisk = p.currentPrice - stop;
        const localRisk = perShareRisk * p.quantity;
        // Convert via the wallet ratio (currentValue is already GBP-converted)
        const riskGbp = currentValue * (localRisk / Math.max(0.01, p.currentPrice * p.quantity));
        protectedRiskGbp += riskGbp;
      }
    } else {
      unprotectedRiskGbp += currentValue;
      unprotectedCount += 1;
    }
  }

  const totalRiskGbp = protectedRiskGbp + unprotectedRiskGbp;
  const hasAnyRisk = totalRiskGbp > 0 || unprotectedCount > 0;

  if (positions.length === 0 || !hasAnyRisk) return null;

  return (
    <Link
      to="/positions"
      className={cn(
        'block rounded-xl border px-4 py-3 transition-colors group',
        unprotectedCount > 0
          ? 'border-rose-500/40 bg-rose-500/5 hover:bg-rose-500/10'
          : 'border-zinc-800 bg-zinc-950 hover:border-zinc-700',
      )}
    >
      <div className="flex items-baseline gap-5 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-wider text-zinc-400">
            Open risk
          </div>
          <div className="text-xl font-semibold text-zinc-100 num">
            {fmtMoney(totalRiskGbp)}
          </div>
          <div className="text-[11px] text-zinc-400 mt-0.5">
            if every stop hits · click to view positions
          </div>
        </div>
        <div className="text-right">
          <div className="text-[11px] uppercase tracking-wider text-zinc-400">
            Unprotected
          </div>
          <div
            className={cn(
              'text-xl font-semibold num inline-flex items-center gap-1.5',
              unprotectedCount > 0 ? 'text-rose-300' : 'text-emerald-300',
            )}
          >
            {unprotectedCount > 0 ? (
              <ShieldOff className="size-4" />
            ) : (
              <ShieldAlert className="size-4 text-emerald-400" />
            )}
            {unprotectedCount}
          </div>
          <div className="text-[11px] text-zinc-400 mt-0.5 num">
            {fmtMoney(unprotectedRiskGbp)} exposed
          </div>
        </div>
        {invalidStopCount > 0 && (
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-wider text-amber-300">
              Stop above market
            </div>
            <div className="text-xl font-semibold text-amber-200 num">
              {invalidStopCount}
            </div>
            <div className="text-[11px] text-zinc-400 mt-0.5">misconfigured</div>
          </div>
        )}
      </div>
    </Link>
  );
}
